import React, { useState, useEffect, useCallback } from 'react';
import { Bell, X, Clock, AlertTriangle, AlertCircle, Info, CheckCircle, ChevronRight, Calendar, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import * as api from '../services/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const ALERTS_PER_PAGE = 10;

const priorityConfig = {
  high: { 
    label: 'Alta', 
    color: 'bg-red-100 text-red-800 border-red-200',
    icon: AlertTriangle,
    iconColor: 'text-red-500',
    badge: 'bg-red-500'
  },
  medium: { 
    label: 'Media', 
    color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    icon: AlertCircle,
    iconColor: 'text-yellow-500',
    badge: 'bg-yellow-500'
  },
  low: { 
    label: 'Baja', 
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    icon: Info,
    iconColor: 'text-blue-500',
    badge: 'bg-blue-500'
  }
};

// Alert Item Component
const AlertItem = ({ alert, onMarkRead, onNavigate }) => {
  const priority = priorityConfig[alert.priority] || priorityConfig.medium;
  const PriorityIcon = priority.icon;
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = date - now;
    const diffDays = Math.ceil(diff / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return `Vencida hace ${Math.abs(diffDays)} día(s)`;
    } else if (diffDays === 0) {
      return 'Hoy';
    } else if (diffDays === 1) {
      return 'Mañana';
    } else if (diffDays <= 7) {
      return `En ${diffDays} días`;
    } else {
      return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
    }
  };
  
  const isOverdue = alert.date && new Date(alert.date) < new Date();
  
  return (
    <div 
      className={`p-3 rounded-lg border ${alert.isRead ? 'bg-slate-50 border-slate-200' : 'bg-white border-slate-200 shadow-sm'} hover:shadow-md transition-all cursor-pointer group`}
      onClick={() => onNavigate(alert)}
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${priority.color}`}>
          <PriorityIcon className={`w-4 h-4 ${priority.iconColor}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className={`font-medium text-sm ${alert.isRead ? 'text-slate-500' : 'text-slate-900'} truncate`}>
              {alert.title}
            </h4>
            {!alert.isRead && (
              <span className="w-2 h-2 bg-indigo-500 rounded-full flex-shrink-0"></span>
            )}
          </div>
          {alert.description && (
            <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{alert.description}</p>
          )}
          <div className="flex items-center gap-3 mt-1.5">
            <span className={`text-xs flex items-center gap-1 ${isOverdue ? 'text-red-600 font-medium' : 'text-slate-400'}`}>
              <Clock className="w-3 h-3" />
              {formatDate(alert.date)}
            </span>
            {alert.leadName && (
              <span className="text-xs text-slate-400 truncate">
                Lead: {alert.leadName}
              </span>
            )}
          </div>
        </div>
        {/* Mark as read button - Always visible for unread alerts */}
        {!alert.isRead && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onMarkRead(alert.id);
            }}
            className="p-1.5 rounded-lg hover:bg-green-100 text-green-500 hover:text-green-600 transition-colors bg-green-50"
            title="Marcar como leída"
          >
            <Check className="w-4 h-4" />
          </button>
        )}
        <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
      </div>
    </div>
  );
};

// Main Alert Modal Component - Optimized with pagination per tab
export const AlertModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');
  const [alertsByTab, setAlertsByTab] = useState({
    all: { alerts: [], loaded: false, page: 1, hasMore: true },
    high: { alerts: [], loaded: false, page: 1, hasMore: true },
    medium: { alerts: [], loaded: false, page: 1, hasMore: true },
    low: { alerts: [], loaded: false, page: 1, hasMore: true },
  });
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState({ all: 0, high: 0, medium: 0, low: 0 });
  
  // Load alerts for a specific tab
  const loadAlertsForTab = useCallback(async (tab, page = 1) => {
    setLoading(true);
    try {
      const allAlerts = await api.getAlerts();
      
      // Filter by priority
      let filtered = allAlerts || [];
      if (tab !== 'all') {
        filtered = filtered.filter(a => a.priority === tab);
      }
      
      // Sort: unread first, then by date
      filtered.sort((a, b) => {
        if (a.isRead !== b.isRead) return a.isRead ? 1 : -1;
        return new Date(a.date) - new Date(b.date);
      });
      
      // Update counts
      setCounts({
        all: allAlerts.filter(a => !a.isRead).length,
        high: allAlerts.filter(a => a.priority === 'high' && !a.isRead).length,
        medium: allAlerts.filter(a => a.priority === 'medium' && !a.isRead).length,
        low: allAlerts.filter(a => a.priority === 'low' && !a.isRead).length,
      });
      
      // Paginate
      const startIndex = 0;
      const endIndex = page * ALERTS_PER_PAGE;
      const paginatedAlerts = filtered.slice(startIndex, endIndex);
      
      setAlertsByTab(prev => ({
        ...prev,
        [tab]: {
          alerts: paginatedAlerts,
          loaded: true,
          page: page,
          hasMore: endIndex < filtered.length,
          total: filtered.length
        }
      }));
    } catch (error) {
      console.error('Error loading alerts:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Load initial data when modal opens
  useEffect(() => {
    if (isOpen) {
      loadAlertsForTab(activeTab, 1);
    }
  }, [isOpen, activeTab, loadAlertsForTab]);
  
  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (!alertsByTab[tab].loaded) {
      loadAlertsForTab(tab, 1);
    }
  };
  
  // Load more alerts
  const loadMore = () => {
    const currentTab = alertsByTab[activeTab];
    if (currentTab.hasMore && !loading) {
      loadAlertsForTab(activeTab, currentTab.page + 1);
    }
  };
  
  const handleMarkRead = async (alertId) => {
    try {
      await api.updateAlert(alertId, { isRead: true });
      // Update all tabs that might contain this alert
      setAlertsByTab(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(tab => {
          updated[tab] = {
            ...updated[tab],
            alerts: updated[tab].alerts.map(a => 
              a.id === alertId ? { ...a, isRead: true } : a
            )
          };
        });
        return updated;
      });
      // Update counts
      setCounts(prev => ({
        ...prev,
        all: Math.max(0, prev.all - 1),
        [alertsByTab[activeTab].alerts.find(a => a.id === alertId)?.priority || 'medium']: 
          Math.max(0, prev[alertsByTab[activeTab].alerts.find(a => a.id === alertId)?.priority || 'medium'] - 1)
      }));
      toast.success('Alerta marcada como leída');
    } catch (error) {
      toast.error('Error al marcar como leída');
    }
  };
  
  const handleNavigateToLead = (alert) => {
    if (alert.leadId) {
      // Solo navegar al lead, NO marcar como leída automáticamente
      // La alerta se marca como leída solo con el botón de chulito (✓)
      onClose();
      // Use correct route - /lead/ not /leads/
      navigate(`/lead/${alert.leadId}`);
    } else {
      toast.info('Esta alerta no tiene un lead asociado');
    }
  };
  
  const currentTabData = alertsByTab[activeTab];
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-indigo-600" />
            Centro de Alertas
          </DialogTitle>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={handleTabChange} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid w-full grid-cols-4 flex-shrink-0">
            <TabsTrigger value="all" className="text-xs relative">
              Todas
              {counts.all > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-slate-200 text-slate-700 rounded-full text-xs">
                  {counts.all}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="high" className="text-xs relative">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                Alta
              </span>
              {counts.high > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-red-100 text-red-700 rounded-full text-xs">
                  {counts.high}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="medium" className="text-xs relative">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                Media
              </span>
              {counts.medium > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded-full text-xs">
                  {counts.medium}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="low" className="text-xs relative">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                Baja
              </span>
              {counts.low > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">
                  {counts.low}
                </span>
              )}
            </TabsTrigger>
          </TabsList>
          
          <div className="flex-1 overflow-y-auto mt-4">
            {loading && !currentTabData.loaded ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="animate-pulse bg-slate-100 h-20 rounded-lg"></div>
                ))}
              </div>
            ) : currentTabData.alerts.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Bell className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">No hay alertas {activeTab !== 'all' && `de prioridad ${priorityConfig[activeTab]?.label.toLowerCase()}`}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {currentTabData.alerts.map(alert => (
                  <AlertItem 
                    key={alert.id} 
                    alert={alert}
                    onMarkRead={handleMarkRead}
                    onNavigate={handleNavigateToLead}
                  />
                ))}
                
                {/* Load More Button */}
                {currentTabData.hasMore && (
                  <button
                    onClick={loadMore}
                    disabled={loading}
                    className="w-full py-2 text-sm text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {loading ? 'Cargando...' : `Cargar más (${currentTabData.total - currentTabData.alerts.length} restantes)`}
                  </button>
                )}
              </div>
            )}
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

// Dashboard Alert Summary Widget
export const AlertSummaryWidget = ({ onOpenAlerts }) => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const loadAlerts = async () => {
      try {
        const alertsArray = await api.getAlerts();
        // Get upcoming and overdue alerts, sorted by date
        const now = new Date();
        const relevantAlerts = (alertsArray || [])
          .filter(a => !a.isRead || new Date(a.date) >= now)
          .sort((a, b) => new Date(a.date) - new Date(b.date))
          .slice(0, 5);
        setAlerts(relevantAlerts);
      } catch (error) {
        console.error('Error loading alerts:', error);
      } finally {
        setLoading(false);
      }
    };
    loadAlerts();
  }, []);
  
  const stats = {
    total: alerts.length,
    high: alerts.filter(a => a.priority === 'high' && !a.isRead).length,
    medium: alerts.filter(a => a.priority === 'medium' && !a.isRead).length,
    low: alerts.filter(a => a.priority === 'low' && !a.isRead).length,
    overdue: alerts.filter(a => new Date(a.date) < new Date()).length,
  };
  
  const formatAlertDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    if (date < now) return 'Vencida';
    if (date.toDateString() === now.toDateString()) return 'Hoy';
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (date.toDateString() === tomorrow.toDateString()) return 'Mañana';
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  };
  
  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm animate-pulse">
        <div className="h-6 bg-slate-200 rounded w-32 mb-4"></div>
        <div className="space-y-3">
          <div className="h-12 bg-slate-200 rounded"></div>
          <div className="h-12 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
          <Bell className="w-5 h-5 text-indigo-600" />
          Alertas Pendientes
        </h3>
        <button 
          onClick={onOpenAlerts}
          className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
        >
          Ver todas
        </button>
      </div>
      
      {/* Stats Summary */}
      <div className="grid grid-cols-4 gap-2 mb-4">
        <div className="text-center p-2 bg-slate-50 rounded-lg">
          <p className="text-lg font-bold text-slate-900">{stats.total}</p>
          <p className="text-xs text-slate-500">Total</p>
        </div>
        <div className="text-center p-2 bg-red-50 rounded-lg">
          <p className="text-lg font-bold text-red-600">{stats.high}</p>
          <p className="text-xs text-red-600">Alta</p>
        </div>
        <div className="text-center p-2 bg-yellow-50 rounded-lg">
          <p className="text-lg font-bold text-yellow-600">{stats.medium}</p>
          <p className="text-xs text-yellow-600">Media</p>
        </div>
        <div className="text-center p-2 bg-blue-50 rounded-lg">
          <p className="text-lg font-bold text-blue-600">{stats.low}</p>
          <p className="text-xs text-blue-600">Baja</p>
        </div>
      </div>
      
      {/* Overdue Warning */}
      {stats.overdue > 0 && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-red-500" />
          <span className="text-sm text-red-700 font-medium">
            {stats.overdue} alerta(s) vencida(s)
          </span>
        </div>
      )}
      
      {/* Alert List */}
      <div className="space-y-2">
        {alerts.length === 0 ? (
          <div className="text-center py-4 text-slate-500">
            <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-400" />
            <p className="text-sm">No hay alertas pendientes</p>
          </div>
        ) : (
          alerts.slice(0, 3).map(alert => {
            const priority = priorityConfig[alert.priority] || priorityConfig.medium;
            const isOverdue = new Date(alert.date) < new Date();
            
            return (
              <div 
                key={alert.id}
                onClick={() => alert.leadId && navigate(`/lead/${alert.leadId}`)}
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
              >
                <span className={`w-2 h-2 rounded-full ${priority.badge}`}></span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{alert.title}</p>
                  <p className="text-xs text-slate-500 truncate">{alert.leadName || 'Sin lead asignado'}</p>
                </div>
                <span className={`text-xs font-medium ${isOverdue ? 'text-red-600' : 'text-slate-500'}`}>
                  {formatAlertDate(alert.date)}
                </span>
              </div>
            );
          })
        )}
      </div>
      
      {alerts.length > 3 && (
        <button 
          onClick={onOpenAlerts}
          className="w-full mt-3 py-2 text-sm text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
        >
          Ver {alerts.length - 3} más
        </button>
      )}
    </div>
  );
};

export default AlertModal;
